import React, { useState, useRef } from 'react';
import { Brain, Zap, ArrowRight, Search } from 'lucide-react';
import clsx from 'clsx';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { sampleMemories } from '@/data/sample-memories';

interface SampleMemory {
  id: string;
  agentId: string;
  type: string;
  content: string;
  importance: number;
  tags: string[];
  sessionId: string;
  createdAt: string;
}

interface MockMemory extends SampleMemory {
  score: number;
}

const getRelevantMemories = (query: string): MockMemory[] => {
  const lowerQuery = query.toLowerCase();

  // Keywords for matching
  const keywordMap: Record<string, string[]> = {
    migration: ['migration', 'database', 'schema'],
    auth: ['auth', 'oauth', 'jwt', 'security', 'authentication'],
    api: ['api', 'health', 'endpoint', 'payment'],
    upload: ['upload', 'image', 'cdn', 'webp'],
    ecommerce: ['ecommerce', 'marketplace', 'nextjs', 'postgres'],
    user: ['user', 'preference', 'communication'],
  };

  const matchedMemories: MockMemory[] = [];

  for (const memory of sampleMemories) {
    let score = 0;
    const contentLower = memory.content.toLowerCase();

    // Check for keyword matches
    for (const [key, keywords] of Object.entries(keywordMap)) {
      if (keywords.some(k => contentLower.includes(k) || lowerQuery.includes(k))) {
        score += 0.3;
      }
    }

    // Check for direct query match in content
    if (contentLower.includes(lowerQuery) && lowerQuery.length > 2) {
      score += 0.5;
    }

    // Check tags
    if (memory.tags.some(tag => lowerQuery.includes(tag))) {
      score += 0.3;
    }

    if (score > 0) {
      matchedMemories.push({ ...memory, score: Math.min(score, 1) });
    }
  }

  // Sort by score and return top 3
  return matchedMemories
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
};

const formatRelativeTime = (isoDate: string): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString();
};

const TypeColors: Record<string, string> = {
  task_progress: 'primary',
  conversation: 'success',
  tool_result: 'warning',
  user_preference: 'default',
  context: 'primary',
};

export const Hero: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MockMemory[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = () => {
    if (!query.trim()) return;

    setIsSearching(true);
    setHasSearched(true);

    // Simulate API delay
    setTimeout(() => {
      const relevant = getRelevantMemories(query);
      setResults(relevant.length > 0 ? relevant : null);
      setIsSearching(false);
    }, 600);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 bg-bg-primary">
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `
              linear-gradient(#4F46E5/0.05 1px, transparent 1px),
              linear-gradient(90deg, #4F46E5/0.05 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Radial gradient glow from bottom */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center bottom, #4F46E5/0.15 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Pulsing glow animation */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none animate-pulse"
        style={{
          background: 'radial-gradient(ellipse at center bottom, #818CF8/0.1 0%, transparent 60%)',
          animationDuration: '4s',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column - headline and CTAs */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 mb-6 fade-up">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">Now in Public Beta</span>
            </div>

            {/* Headline */}
            <h1
              className="text-4xl sm:text-5xl lg:text-[52px] font-bold leading-[1.1] tracking-tight text-text-primary mb-6 fade-up"
              style={{ animationDelay: '80ms' }}
            >
              Give Your AI Agents a Memory That{' '}
              <span className="text-primary">Never Forgets</span>
            </h1>

            {/* Subheadline */}
            <p
              className="text-lg text-text-secondary leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 fade-up"
              style={{ animationDelay: '160ms' }}
            >
              Standardized persistence layer for cross-provider agent memory.
              Remember context across sessions, transfer state between Claude and GPT.
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10 fade-up"
              style={{ animationDelay: '240ms' }}
            >
              <Button variant="primary" size="lg" href="#get-started">
                Start Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="text-text-secondary hover:text-primary"
                href="#docs"
              >
                Read the Docs
              </Button>
            </div>

            {/* Trust Badges */}
            <div
              className="flex flex-wrap gap-3 justify-center lg:justify-start fade-up"
              style={{ animationDelay: '320ms' }}
            >
              {[
                'Anthropic Ready',
                'OpenAI Compatible',
                'MCP Certified',
                'SOC 2',
              ].map((badge) => (
                <div
                  key={badge}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border text-xs font-medium text-text-secondary"
                >
                  <svg className="h-3.5 w-3.5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {badge}
                </div>
              ))}
            </div>
          </div>

          {/* Right column - API Playground */}
          <div
            className="fade-up"
            style={{ animationDelay: '200ms' }}
          >
            <div className="bg-white rounded-2xl border border-border shadow-xl p-6 sm:p-8">
              {/* Playground header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-50">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">Memory Playground</h3>
                  <p className="text-xs text-text-muted">Try the recall API instantly</p>
                </div>
              </div>

              {/* Input area */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-text-secondary">
                  What task is the agent working on?
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g., database migration, auth flow, image upload..."
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-surface-secondary/30 text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSearch}
                  loading={isSearching}
                  disabled={!query.trim()}
                  className="w-full"
                >
                  Recall memories
                </Button>
              </div>

              {/* Results panel */}
              {hasSearched && (
                <div className="mt-6 pt-6 border-t border-border">
                  {isSearching ? (
                    <div className="flex items-center justify-center py-8 text-text-muted text-sm">
                      <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mr-3" />
                      Searching memories...
                    </div>
                  ) : results && results.length > 0 ? (
                    <div className="space-y-3">
                      <div className="text-xs font-medium text-text-muted uppercase tracking-wide">
                        {results.length} relevant {results.length === 1 ? 'memory' : 'memories'} found
                      </div>
                      {results.map((memory, idx) => (
                        <div
                          key={memory.id}
                          className="p-4 rounded-lg bg-surface-secondary/50 border border-border/50 hover:border-primary/30 transition-colors"
                          style={{
                            animation: `fadeUp 0.3s ease-out ${idx * 100}ms both`,
                          }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <Badge variant={TypeColors[memory.type] as any}>
                              {memory.type.replace('_', ' ')}
                            </Badge>
                            <span className="text-xs text-text-muted">
                              {formatRelativeTime(memory.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-text-primary leading-relaxed line-clamp-2">
                            {memory.content}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {memory.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-primary/80"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-text-muted text-sm">
                        No memories found for "{query}"
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        Try a different query like "migration" or "auth"
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Empty state hint */}
              {!hasSearched && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-center text-text-muted text-sm">
                    Results will appear here after you search
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center mt-3">
                    {['migration', 'auth', 'api', 'upload'].map((hint) => (
                      <button
                        key={hint}
                        onClick={() => setQuery(hint)}
                        className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-primary hover:bg-indigo-100 transition-colors cursor-pointer"
                      >
                        {hint}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-bg-primary to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;